import React, { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  Collapse,
  List,
  Spin,
  Typography,
  Row,
  Col,
  Alert,
  Grid,
  Checkbox,
} from "antd";
import {
  defaultIfEmpty,
  formatNumber,
} from "../../../Shared/Helpers/StringHelpers";
import { MapService, GeologicMapResult } from "../Services/MapService";
import { PlanarianTag } from "../../../Shared/Components/Display/PlanarianTag";
import {
  PlanarianDescription,
  type PlanarianDescriptionItem,
} from "../../../Shared/Components/Buttons/PlanarianDescription";
import { PlanarianButton } from "../../../Shared/Components/Buttons/PlanarianButtton";
import { HttpHelpers } from "../../../Shared/Helpers/HttpHelpers";
import "./Macrostrat.scss";

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const allOptionValue = "all";

interface MacrostratResponse {
  success: {
    data: {
      elevation: number;
      mapData: any[];
      regions: any[];
      hasColumns: boolean;
    };
  };
}

interface XDDResponse {
  success: {
    data: any[];
  };
}

interface MacrostratProps {
  lat: number;
  lng: number;
  openByDefault?: boolean;
}

interface ExternalHttpLinkProps {
  url: string | null | undefined;
  children: React.ReactNode;
}

const ExternalHttpLink: React.FC<ExternalHttpLinkProps> = ({
  url,
  children,
}) => {
  const safeUrl = HttpHelpers.GetSafeExternalHttpUrl(url);
  return safeUrl ? (
    <a href={safeUrl} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <>{children}</>
  );
};

interface ExpandableTextProps {
  text: string;
  maxChars?: number;
}

/**
 * ExpandableText splits the text into paragraphs (by newline).
 * If the total character count exceeds maxChars, it shows a truncated
 * version along with a "Show more" control. When expanded, it shows all paragraphs
 * with a "Show less" control.
 */
const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  maxChars = 1000,
}) => {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = text
    .split("\\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const totalChars = paragraphs.reduce((acc, para) => acc + para.length, 0);

  if (totalChars <= maxChars) {
    return (
      <>
        {paragraphs.map((para, idx) => (
          <Paragraph key={idx}>{para}</Paragraph>
        ))}
      </>
    );
  }

  if (!expanded) {
    let currentLength = 0;
    const truncatedParagraphs: string[] = [];
    for (const para of paragraphs) {
      if (currentLength + para.length <= maxChars) {
        truncatedParagraphs.push(para);
        currentLength += para.length;
      } else {
        const remaining = maxChars - currentLength;
        if (remaining > 0) {
          truncatedParagraphs.push(para.substring(0, remaining) + "...");
        }
        break;
      }
    }
    return (
      <>
        {truncatedParagraphs.map((para, idx) => (
          <Paragraph key={idx}>{para}</Paragraph>
        ))}
        <PlanarianButton
          alwaysShowChildren
          icon={undefined}
          type="link"
          style={{ padding: 0 }}
          onClick={() => setExpanded(true)}
        >
          Show more
        </PlanarianButton>
      </>
    );
  }

  return (
    <>
      {paragraphs.map((para, idx) => (
        <Paragraph key={idx}>{para}</Paragraph>
      ))}
      <PlanarianButton
        alwaysShowChildren
        icon={undefined}
        type="link"
        style={{ padding: 0 }}
        onClick={() => setExpanded(false)}
      >
        Show less
      </PlanarianButton>
    </>
  );
};

const Macrostrat: React.FC<MacrostratProps> = ({
  lat,
  lng,
  openByDefault = true,
}) => {
  const screens = useBreakpoint();
  const descriptionLayout = screens.md ? "horizontal" : "vertical";

  const [macroData, setMacroData] = useState<MacrostratResponse | null>(null);
  const [xddData, setXddData] = useState<XDDResponse | null>(null);
  const [geologicMapsData, setGeologicMapsData] = useState<GeologicMapResult[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [geologicMapsLoading, setGeologicMapsLoading] =
    useState<boolean>(false);
  const [geologicMapsError, setGeologicMapsError] = useState<string | null>(
    null
  );
  const [selectedScales, setSelectedScales] = useState<(number | string)[]>([]); // Changed type

  const isColorLight = (color: string): boolean => {
    if (!color || color[0] !== "#") return false;
    const r = parseInt(color.substr(1, 2), 16);
    const g = parseInt(color.substr(3, 2), 16);
    const b = parseInt(color.substr(5, 2), 16);
    // Calculate brightness per ITU-R BT.601
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 200;
  };

  const getTagStyle = (color?: string) => {
    return color && isColorLight(color)
      ? {
          border: "1px solid rgba(0,0,0,0.2)",
          color: "rgba(0,0,0,0.7)",
        }
      : {};
  };

  const fetchMacrostratData = async () => {
    try {
      setLoading(true);
      setGeologicMapsLoading(true);
      setError(null);
      setGeologicMapsError(null);

      const macrostratUrl = `https://macrostrat.org/api/v2/mobile/map_query_v2?lng=${lng}&lat=${lat}&z=9`;
      const resp = await fetch(macrostratUrl);
      if (!resp.ok) {
        throw new Error(`Macrostrat fetch error: ${resp.statusText}`);
      }
      const data = (await resp.json()) as MacrostratResponse;
      setMacroData(data);

      try {
        let maps = await MapService.getGeologicMaps(lat, lng);

        // Sort maps:
        // 1. Primary sort by scale: ascending for positive scales, -1 (unscaled) goes to the end.
        // 2. Secondary sort by year: descending (newer first).
        maps.sort((a, b) => {
          const aIsUnscaled = a.scale === -1;
          const bIsUnscaled = b.scale === -1;

          // Rule 1: Unscaled items go to the end
          if (aIsUnscaled && !bIsUnscaled) return 1; // a (-1) comes after b (positive)
          if (!aIsUnscaled && bIsUnscaled) return -1; // a (positive) comes before b (-1)

          // Rule 2: If scales are different (applies only if both are positive and different)
          // If both are unscaled, their scales are "equal" for this check (both -1)
          if (a.scale !== b.scale && !aIsUnscaled && !bIsUnscaled) {
            return a.scale - b.scale; // Sort positive scales numerically ascending
          }

          // Rule 3: If scales are effectively the same (both -1, or same positive value), sort by year descending
          return b.year - a.year;
        });

        setGeologicMapsData(maps);
      } catch (mapErr: any) {
        setGeologicMapsError(mapErr.message || "Failed to load geologic maps.");
        setGeologicMapsData([]);
      } finally {
        setGeologicMapsLoading(false);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchXDDData = async (term: string) => {
    try {
      setLoading(true);
      setError(null);
      const queryTerm = encodeURIComponent(term);
      const xddUrl = `https://xdd.wisc.edu/api/v1/snippets?article_limit=20&term=${queryTerm}`;
      const resp = await fetch(xddUrl);
      if (!resp.ok) {
        throw new Error(`XDD fetch error: ${resp.statusText}`);
      }
      const data = (await resp.json()) as XDDResponse;
      setXddData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const xddSnippets = xddData?.success.data || [];

  // Get unique scales for the filter dropdown, maintaining the sort order (finest first, -1 last)
  const uniqueScales = useMemo(() => {
    const scales: number[] = [];
    if (geologicMapsData.length > 0) {
      const scaleSet = new Set<number>();
      geologicMapsData.forEach((map) => {
        if (!scaleSet.has(map.scale)) {
          scales.push(map.scale);
          scaleSet.add(map.scale);
        }
      });
    }
    return scales;
  }, [geologicMapsData]);

  useEffect(() => {
    if (uniqueScales.length > 0) {
      const finestScale = uniqueScales[0];
      if (uniqueScales.length === 1) {
        // If only one unique scale, select it and "All"
        setSelectedScales([finestScale, allOptionValue]);
      } else {
        // Otherwise, select only the finest scale
        setSelectedScales([finestScale]);
      }
    } else {
      setSelectedScales([]);
    }
  }, [uniqueScales]);

  const handleScaleChange = (checkedList: (number | string)[]) => {
    const allWasPreviouslyInSelectedScales =
      selectedScales.includes(allOptionValue);
    const allIsCurrentlyInCheckedList = checkedList.includes(allOptionValue);
    const allUniqueScalesAvailable = uniqueScales.length > 0;

    let newSelectedValues: (number | string)[];

    if (allIsCurrentlyInCheckedList && !allWasPreviouslyInSelectedScales) {
      // "All" was just explicitly checked by the user
      newSelectedValues = allUniqueScalesAvailable
        ? [...uniqueScales, allOptionValue]
        : [allOptionValue];
    } else if (
      !allIsCurrentlyInCheckedList &&
      allWasPreviouslyInSelectedScales
    ) {
      // "All" was just explicitly unchecked by the user
      // Revert to selecting only the highest resolution scale (or none if no scales)
      newSelectedValues = allUniqueScalesAvailable ? [uniqueScales[0]] : [];
    } else {
      // "All" was not the item directly clicked, or its state in checkedList matches its previous state.
      // This means an individual scale was checked/unchecked.
      const currentlySelectedNumeric = checkedList.filter(
        (v) => typeof v === "number"
      ) as number[];
      newSelectedValues = [...currentlySelectedNumeric];

      if (
        allUniqueScalesAvailable &&
        currentlySelectedNumeric.length === uniqueScales.length
      ) {
        // All individual scales are now checked, so add "all" to the selection
        if (!newSelectedValues.includes(allOptionValue)) {
          newSelectedValues.push(allOptionValue);
        }
      }
    }
    setSelectedScales(newSelectedValues);
  };

  const filteredGeologicMaps =
    selectedScales.filter((s) => typeof s === "number").length > 0
      ? geologicMapsData.filter((map) => selectedScales.includes(map.scale))
      : geologicMapsData;

  const groupOptions = [
    ...uniqueScales.map((scale) => ({
      label: scale === -1 ? "N/A" : formatNumber(scale),
      value: scale,
    })),
    ...(uniqueScales.length > 0
      ? [{ label: "All", value: allOptionValue, style: { marginRight: "8px" } }]
      : []),
  ];

  useEffect(() => {
    fetchMacrostratData();
  }, [lat, lng]);

  useEffect(() => {
    if (macroData) {
      const firstMap = macroData.success.data.mapData[0];
      if (firstMap?.strat_name) {
        fetchXDDData(firstMap.strat_name);
      }
    }
  }, [macroData]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 50 }}>
        <Spin tip="Loading data..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error fetching data"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (!macroData) {
    return null; // Or a <Text>No data available</Text> component
  }

  const { mapData = [], regions = [] } = macroData.success.data;
  const firstMap = mapData[0] || {};

  const {
    name,
    age,
    strat_name,
    lith, // e.g., "Major:{limestone}"
    descrip,
    comments,
    liths: mainLiths = [],
    color, // main color for the map object
    b_int,
    t_int,
    ref, // source for the geologic map
  } = firstMap;

  // Macrostrat-specific fields
  const {
    strat_names = [],
    max_thick,
    min_min_thick,
    b_age,
    t_age,
    liths: macroLiths = [],
    environs = [],
  } = firstMap.macrostrat || {};

  const descriptionText = descrip ? descrip.trim() : "";
  const commentsText = comments ? comments.trim() : "";

  // Compute ranges for macrostrat age and thickness (if available)
  const macroAgeRange = b_age && t_age ? `${b_age}–${t_age} Ma` : "N/A";
  const thicknessRange =
    typeof min_min_thick === "number" && typeof max_thick === "number"
      ? `${min_min_thick} – ${max_thick} m`
      : defaultIfEmpty("");

  const geologicMapsContent = geologicMapsLoading ? (
    <Text>Loading...</Text>
  ) : geologicMapsError ? (
    <Text type="danger">{geologicMapsError}</Text>
  ) : geologicMapsData.length > 0 ? (
    <>
      <div>
        <Text>Scale: </Text>
        <Checkbox.Group
          options={groupOptions}
          value={selectedScales}
          onChange={handleScaleChange}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        />
      </div>
      {filteredGeologicMaps.map((map) => (
        <a
          key={map.id}
          href={`https://ngmdb.usgs.gov/Prodesc/proddesc_${map.id}.htm`}
          target="_blank"
          rel="noreferrer"
          className="macrostrat-geologic-map-link"
        >
          <div>
            <Row gutter={[16, 16]} style={{ width: "100%" }} align="middle">
              <Col
                xs={24}
                sm={24}
                md={map.thumbnail ? 16 : 24}
                lg={map.thumbnail ? 18 : 24}
              >
                <Typography>
                  {" "}
                  <Text>{map.title}</Text>
                  <br />
                  <Text type="secondary">
                    Scale: {map.scale === -1 ? "N/A" : formatNumber(map.scale)}
                  </Text>
                  <br />
                  <Text type="secondary">Authors: {map.authors}</Text>
                  <br />
                  <Text type="secondary">
                    Source: {map.publisher} ({map.series}, {map.year})
                  </Text>
                </Typography>
              </Col>
              {map.thumbnail && (
                <Col xs={24} sm={24} md={8} lg={6}>
                  <img
                    style={{
                      maxHeight: "300px",
                      maxWidth: "100%",
                      display: "block",
                      margin:
                        screens.xs || screens.sm ? "8px auto 0" : "0 auto",
                    }}
                    src={`https://ngmdb.usgs.gov${map.thumbnail}`}
                    alt={map.title}
                  />
                </Col>
              )}
            </Row>
          </div>
        </a>
      ))}
    </>
  ) : (
    defaultIfEmpty(null)
  );

  const geologicMapItems: PlanarianDescriptionItem[] = [
    { key: "name", label: "Name", children: name },
    {
      key: "ngmdb-geologic-maps",
      label: "NGMDB Geologic Maps",
      span: 1,
      children: geologicMapsContent,
    },
    {
      key: "age",
      label: "Age",
      children: (
        <PlanarianTag color={color || "default"} style={getTagStyle(color)}>
          {age}
        </PlanarianTag>
      ),
    },
    {
      key: "interval",
      label: "Interval",
      children:
        b_int && t_int ? (
          <PlanarianTag color={b_int.color} style={getTagStyle(b_int.color)}>
            {b_int.int_name} ({b_int.b_age}–{t_int.t_age} Ma)
          </PlanarianTag>
        ) : (
          defaultIfEmpty("")
        ),
    },
    {
      key: "stratigraphic-names",
      label: "Stratigraphic Name(s)",
      children: strat_name,
    },
    { key: "lithology", label: "Lithology", children: lith },
    {
      key: "lithology-details",
      label: "Lithology Details",
      children:
        mainLiths.length > 0
          ? mainLiths.map((l: any, idx: number) => (
              <PlanarianTag
                key={idx}
                color={l.color || "default"}
                style={getTagStyle(l.color)}
              >
                {l.lith} ({l.lith_type})
              </PlanarianTag>
            ))
          : defaultIfEmpty(""),
    },
    {
      key: "description",
      label: "Description",
      children: <ExpandableText text={descriptionText} maxChars={300} />,
    },
    {
      key: "comments",
      label: "Comments",
      children: <ExpandableText text={commentsText} maxChars={300} />,
    },
    {
      key: "source",
      label: "Source",
      children: ref ? (
        <ExternalHttpLink url={ref.url}>
          {ref.name} ({ref.ref_source}, {ref.ref_year})
        </ExternalHttpLink>
      ) : (
        defaultIfEmpty("")
      ),
    },
  ];

  const macrostratItems: PlanarianDescriptionItem[] = [
    {
      key: "matched-stratigraphic-unit",
      label: "Matched Stratigraphic Unit",
      children: strat_names.map((sn: any) => sn.rank_name).join(", "),
    },
    {
      key: "age",
      label: "Age",
      children: (
        <PlanarianTag
          color={(b_int && b_int.color) || "default"}
          style={getTagStyle(b_int && b_int.color)}
        >
          {macroAgeRange}
        </PlanarianTag>
      ),
    },
    { key: "thickness", label: "Thickness", children: thicknessRange },
    {
      key: "base-interval",
      label: "Base Interval",
      children: b_int ? (
        <PlanarianTag color={b_int.color} style={getTagStyle(b_int.color)}>
          {b_int.int_name}
        </PlanarianTag>
      ) : (
        defaultIfEmpty("")
      ),
    },
    {
      key: "top-interval",
      label: "Top Interval",
      children: t_int ? (
        <PlanarianTag color={t_int.color} style={getTagStyle(t_int.color)}>
          {t_int.int_name}
        </PlanarianTag>
      ) : (
        defaultIfEmpty("")
      ),
    },
    {
      key: "lithology-parsed",
      label: "Lithology (parsed)",
      children:
        macroLiths.length > 0
          ? macroLiths.map((l: any, idx: number) => (
              <PlanarianTag
                key={idx}
                color={l.color || "default"}
                style={getTagStyle(l.color)}
              >
                {l.lith} ({l.lith_type})
              </PlanarianTag>
            ))
          : defaultIfEmpty(""),
    },
    {
      key: "environments",
      label: "Environments",
      children:
        environs.length > 0
          ? environs.map((env: any, idx: number) => (
              <PlanarianTag
                key={idx}
                color={env.color || "default"}
                style={getTagStyle(env.color)}
              >
                {env.environ}
              </PlanarianTag>
            ))
          : defaultIfEmpty(""),
    },
  ];

  const getSnippetItems = (snippet: any): PlanarianDescriptionItem[] => [
    { key: "title", label: "Title", children: snippet.title },
    { key: "authors", label: "Authors", children: snippet.authors },
    {
      key: "doi",
      label: "DOI",
      children: (
        <a
          href={snippet.doi ? `https://doi.org/${snippet.doi}` : "#"}
          target="_blank"
          rel="noreferrer"
        >
          {snippet.doi || "N/A"}
        </a>
      ),
    },
    {
      key: "highlights",
      label: "Highlights",
      children: (
        <List
          dataSource={snippet.highlight}
          renderItem={(highlight: string, hIdx) => (
            <List.Item key={hIdx}>
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(highlight, {
                    ALLOWED_TAGS: ["mark", "em", "br"],
                    ALLOWED_ATTR: [],
                    ALLOW_DATA_ATTR: false,
                    ALLOW_ARIA_ATTR: false,
                  }),
                }}
              />
            </List.Item>
          )}
        />
      ),
    },
  ];

  const getRegionItems = (region: any): PlanarianDescriptionItem[] => {
    const items: PlanarianDescriptionItem[] = [
      { key: "group", label: "Group", children: region.boundary_group },
      { key: "type", label: "Type", children: region.boundary_type },
      { key: "class", label: "Class", children: region.boundary_class },
      { key: "description", label: "Description", children: region.descrip },
    ];

    if (region.wiki_link) {
      items.push({
        key: "wiki-link",
        label: "Wiki Link",
        children: (
          <ExternalHttpLink url={region.wiki_link}>
            {region.wiki_link}
          </ExternalHttpLink>
        ),
      });
    }

    return items;
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Collapse defaultActiveKey={openByDefault ? ["1"] : []}>
            <Panel header="Geologic map" key="1">
              <PlanarianDescription
                copyable={false}
                layout={descriptionLayout}
                column={1}
                size="small"
                items={geologicMapItems}
              />
              <Typography>
                {" "}
                Macrostrat coverage varies by region and may be less detailed
                than traditional geologic maps.
              </Typography>
            </Panel>

            {/* Macrostrat-linked data panel */}
            <Panel header="Macrostrat-linked data" key="2">
              <PlanarianDescription
                copyable={false}
                layout={descriptionLayout}
                column={1}
                size="small"
                items={macrostratItems}
              />
            </Panel>

            {/* Primary Literature panel */}
            <Panel header="Primary Literature" key="3">
              {xddSnippets.length === 0 ? (
                <Text>No references found for "{strat_name}"</Text>
              ) : (
                <Collapse accordion>
                  {xddSnippets.map((snippet: any, idx: number) => (
                    <Panel
                      header={
                        <span>
                          {snippet.title} <br />
                          <Text type="secondary">{snippet.pubname}</Text>
                        </span>
                      }
                      key={idx}
                    >
                      <PlanarianDescription
                        copyable={false}
                        layout={descriptionLayout}
                        column={1}
                        size="small"
                        items={getSnippetItems(snippet)}
                      />
                    </Panel>
                  ))}
                </Collapse>
              )}
            </Panel>

            {/* Regions panel */}
            {regions.length > 0 && (
              <Panel header="Physiography / Regions" key="4">
                <Collapse>
                  {regions.map((region: any, index: number) => (
                    <Panel header={region.name} key={index}>
                      <PlanarianDescription
                        copyable={false}
                        layout={descriptionLayout}
                        column={1}
                        size="small"
                        items={getRegionItems(region)}
                      />
                    </Panel>
                  ))}
                </Collapse>
              </Panel>
            )}
          </Collapse>
        </Col>
      </Row>
    </div>
  );
};

export { Macrostrat };
